/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox, ICheckboxProps, ICheckboxStyleProps, ICheckboxStyles } from '@fluentui/react/lib/Checkbox';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { Region } from 'src/core/types/regionTypes';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import {
    summarizationOverviewLink, // summarizationRequestAccessLink,
    textAnalyticsPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import { SummaryAspects } from 'src/languageStudio/core/services/conversation/conversationSwaggers';

type ConversationSummarizationAspectsOptionsProps = {
    isDisabled: boolean;
    currentRegion: Region;
    selectedSummarizationType: SummaryAspects[];
    onSummarizationTypeChange: (newType: SummaryAspects[]) => void;
};

const messages = defineMessages({
    summaryType: { id: 'languageStudio.conversationSummarizationAspectsOptions.summaryType', defaultMessage: 'Summary Aspects' },
    numberOfSentencesDescription: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.numberOfSentencesDescription',
        defaultMessage:
            'The number of sentences is just a suggestion to generative summarization skills, may not be reflected in the generated summary.'
    },
    description: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.description',
        defaultMessage: 'Multiple aspects can be selected. These options may incur additional costs.'
    },
    /* approvedAccess: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.approvedAccess',
        defaultMessage: 'Request access to preview in Switzerland North'
    }, */
    pricingLearnMore: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.pricingLearnMore',
        defaultMessage: 'Learn more about pricing'
    },
    issueSummaryType: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.issueSummaryType',
        defaultMessage: `Issue`
    },
    issueSummaryTypeSubLabel: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.issueSummaryTypeSubLabel',
        defaultMessage: 'Summarizes a customer issue in a customer-and-agent conversation'
    },
    resolutionSummaryType: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.resolutionSummaryType',
        defaultMessage: 'Resolution'
    },
    resolutionSummaryTypeSubLabel: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.resolutionSummaryTypeSubLabel',
        defaultMessage: 'Summarizes solutions tried in a customer-and-agent conversation'
    },
    recapSummaryType: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.recapSummaryType',
        defaultMessage: 'Recap'
    },
    recapSummaryTypeSubLabel: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.recapSummaryTypeSubLabel',
        defaultMessage: 'Summarizes a conversation into one paragraph'
    },
    followUpSummaryType: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.followUpSummaryType',
        defaultMessage: 'Follow-up tasks (Preview)'
    },
    followUpTypeSubLabel: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.followUpTypeSubLabel',
        defaultMessage: 'Summarizes next action items agreed in conversation'
    },
    numberOfSentences: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.numberOfSentences',
        defaultMessage: 'Number of sentences'
    },
    chapterSummaryType: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.chapterSummaryType',
        defaultMessage: 'Chapter title'
    },
    chapterSummaryTypeSubLabel: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.chapterSummaryTypeSubLabel',
        defaultMessage: 'Segments a conversation into sections headed by titles.'
    },
    narrativeSummaryType: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.narrativeSummaryType',
        defaultMessage: 'Narrative'
    },
    narrativeSummaryTypeSubLabel: {
        id: 'languageStudio.conversationSummarizationAspectsOptions.narrativeSummaryTypeSubLabel',
        defaultMessage: 'Segments a conversation and generates a summary for each segment'
    }
});

const optionStyles = classNamesFunction<ICheckboxStyleProps, ICheckboxStyles>()({ label: { alignItems: 'flex-start' } });

export const ConversationSummarizationAspectsOptions = (props: ConversationSummarizationAspectsOptionsProps) => {
    const { currentRegion, selectedSummarizationType, isDisabled, onSummarizationTypeChange } = props;
    const { localize } = useLocalization();

    const notSupportedResourceRegionContent = (
        <LuisText>
            {localize(commonDemosMessages.resourceRegionDetailedError, { region: currentRegion })}&nbsp;
            <ExternalLink href={summarizationOverviewLink}>{localize(commonDemosMessages.seeRegionsLink)}</ExternalLink>&nbsp;
            {localize(commonDemosMessages.resourceRegionDetailedErrorPart2)}
        </LuisText>
    );

    const viewOptions: (ICheckboxProps & { subLabel: string })[] = [
        {
            key: 'recap',
            disabled: isDisabled,
            styles: optionStyles,
            label: localize(messages.recapSummaryType),
            subLabel: localize(messages.recapSummaryTypeSubLabel)
        },
        {
            key: 'issue',
            disabled: isDisabled,
            styles: optionStyles,
            label: localize(messages.issueSummaryType),
            subLabel: localize(messages.issueSummaryTypeSubLabel)
        },
        {
            key: 'resolution',
            disabled: isDisabled,
            styles: optionStyles,
            label: localize(messages.resolutionSummaryType),
            subLabel: localize(messages.resolutionSummaryTypeSubLabel)
        },
        /* { not supported until latency issues are resolved
            key: 'follow-up tasks',
            disabled: isDisabled,
            styles: optionStyles,
            label: localize(messages.followUpSummaryType),
            subLabel: localize(messages.followUpTypeSubLabel)
        }, */
        {
            key: 'chapterTitle',
            disabled: isDisabled,
            styles: optionStyles,
            label: localize(messages.chapterSummaryType),
            subLabel: localize(messages.chapterSummaryTypeSubLabel)
        },
        {
            key: 'narrative',
            disabled: isDisabled,
            styles: optionStyles,
            label: localize(messages.narrativeSummaryType),
            subLabel: localize(messages.narrativeSummaryTypeSubLabel)
        }
    ];

    const handleChangeType = (typeKeys: SummaryAspects[]) => {
        logDemoButtonClick('Summarization conversation', 'SummaryAspect', typeKeys.join(', '));
        onSummarizationTypeChange(typeKeys);
    };

    return (
        <Stack styles={{ root: { flex: 2 } }} tokens={{ childrenGap: 17 }}>
            <LuisText fontWeight={600}>{localize(messages.summaryType)}</LuisText>
            <LuisText fontWeight={400}>
                {localize(messages.description)}&nbsp;
                <ExternalLink href={textAnalyticsPricingLink}>{localize(messages.pricingLearnMore)}</ExternalLink>
            </LuisText>
            {/* <ExternalLink href={summarizationRequestAccessLink}>{localize(messages.approvedAccess)}</ExternalLink>&nbsp; */}
            {viewOptions.map(option => (
                <LuisTooltip hidden={!option.disabled} content={notSupportedResourceRegionContent}>
                    <Stack tokens={{ childrenGap: 8 }}>
                        <Checkbox
                            {...option}
                            checked={selectedSummarizationType.includes(option.key as SummaryAspects)}
                            onChange={(_, checked) =>
                                handleChangeType(
                                    checked
                                        ? [...selectedSummarizationType, option.key as SummaryAspects]
                                        : selectedSummarizationType.filter(j => j !== option.key)
                                )
                            }
                        />
                        <LuisText fontWeight={300} isDisabled={isDisabled} styles={{ root: { marginLeft: '28px' } }}>
                            {option.subLabel}
                        </LuisText>
                    </Stack>
                </LuisTooltip>
            ))}
        </Stack>
    );
};
